import { Plus } from "lucide-react";
import { useRef, useState } from "react";

import type {
  BuilderLegalEntityDealEvent,
  BuilderLegalStructure,
  BuilderLegalStructureEntity,
  BuilderLegalStructureEntityEvidence,
  BuilderLegalStructureGroup,
  BuilderLegalStructureSourceDocument,
} from "@/lib/api/databook";

import {
  BusinessCurrencyPill,
  LegalEntityCard,
  type LegalEntityDraftAttachment,
} from "./LegalEntityCard";
import type {
  FileCoverageStatus,
  LegalEntityFileCategory,
  LegalEntityFileCoverageProps,
} from "./LegalEntityFileCoverage";
import {
  categorizeSources,
  coverageYearLabel,
  monthlyCoverageRows,
} from "./legalEntitySourceCategories";
import {
  addLegalEntity,
  isMidPeriodAcquisition,
  isMidPeriodDisposal,
  moveLegalEntity,
  removeLegalEntity,
  type LegalStructureAnalysisInterval,
  updateLegalEntity,
  updateLegalGroup,
  validateLegalStructure,
  visibleLegalEntityHighlights,
} from "./legalStructure";

interface LegalStructureEditorProps {
  value: BuilderLegalStructure;
  evidence: Record<string, BuilderLegalStructureEntityEvidence>;
  consolidationSources?: BuilderLegalStructureSourceDocument[];
  consolidationCurrencies?: string[];
  analysisInterval?: LegalStructureAnalysisInterval;
  editing: boolean;
  dirty?: boolean;
  saving?: boolean;
  attachmentsBusy?: boolean;
  attachmentsInvalid?: boolean;
  canEdit: boolean;
  error?: string | null;
  onChange: (value: BuilderLegalStructure) => void;
  onEdit: () => void;
  onSave: () => void;
  onConfirmPresentationCurrency: () => void;
  onCancel: () => void;
  onContinue: () => void;
  onDownloadSource?: (source: BuilderLegalStructureSourceDocument) => void;
  persistedEntityIds?: ReadonlySet<string>;
  draftAttachments?: Readonly<Record<string, LegalEntityDraftAttachment[]>>;
  onAttachFiles?: (entityId: string, files: File[]) => void;
  onDetachAttachment?: (entityId: string, attachmentKey: string) => void;
  onDownloadAttachment?: (
    entityId: string,
    attachment: LegalEntityDraftAttachment,
  ) => void;
}

const COVERAGE_YEARS = ["FY23", "FY24", "FY25"];
const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;

interface AddLegalEntityCardProps {
  groups: BuilderLegalStructureGroup[];
  onAdd: (
    name: string,
    options: {
      operatingCurrency: string;
      activitySummary: string | null;
      displayGroupId: string | null;
      dealEvents: BuilderLegalEntityDealEvent[];
    },
  ) => void;
  onCancel: () => void;
}

function AddLegalEntityCard({
  groups,
  onAdd,
  onCancel,
}: AddLegalEntityCardProps) {
  const [name, setName] = useState("");
  const [operatingCurrency, setOperatingCurrency] = useState("");
  const [activitySummary, setActivitySummary] = useState("");
  const [displayGroupId, setDisplayGroupId] = useState("");
  const [dealEventType, setDealEventType] = useState<
    BuilderLegalEntityDealEvent["type"] | ""
  >("");
  const [dealEventMonth, setDealEventMonth] = useState("");

  return (
    <form
      aria-label="Add a missing legal entity"
      className="w-[236px] space-y-2 rounded-xl border border-foreground bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim() || !CURRENCY_CODE_PATTERN.test(operatingCurrency)) return;
        onAdd(name, {
          operatingCurrency,
          activitySummary: activitySummary.trim() || null,
          displayGroupId: displayGroupId || null,
          dealEvents: dealEventType
            ? [{
                type: dealEventType,
                effective_month: dealEventMonth || null,
              }]
            : [],
        });
      }}
    >
      <p className="text-[11.5px] font-semibold">Add an entity</p>
      <input
        autoFocus
        aria-label="New legal entity name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Name"
        className="w-full rounded-lg bg-muted px-2 py-1.5 text-[10.5px] outline-none"
      />
      <input
        aria-label="New legal entity operating currency"
        value={operatingCurrency}
        onChange={(event) =>
          setOperatingCurrency(event.target.value.toUpperCase())}
        placeholder="Operating currency"
        maxLength={3}
        className="w-full rounded-lg bg-muted px-2 py-1.5 text-[10.5px] outline-none"
      />
      <textarea
        aria-label="New legal entity activity"
        value={activitySummary}
        onChange={(event) => setActivitySummary(event.target.value)}
        placeholder="What this entity does"
        className="h-[46px] w-full resize-none rounded-lg bg-muted px-2 py-1.5 text-[10.5px] outline-none"
      />
      <select
        aria-label="New legal entity group"
        value={displayGroupId}
        onChange={(event) => setDisplayGroupId(event.target.value)}
        className="w-full rounded-lg bg-muted px-2 py-1.5 text-[10px] outline-none"
      >
        <option value="">No display group</option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      <select
        aria-label="New legal entity deal event"
        value={dealEventType}
        onChange={(event) => {
          setDealEventType(
            event.target.value as BuilderLegalEntityDealEvent["type"] | "",
          );
          if (!event.target.value) setDealEventMonth("");
        }}
        className="w-full rounded-lg bg-muted px-2 py-1.5 text-[10px] outline-none"
      >
        <option value="">No deal event</option>
        <option value="acquisition">Acquisition</option>
        <option value="disposal">Disposal</option>
      </select>
      {dealEventType && (
        <input
          type="month"
          aria-label="New legal entity deal event month"
          value={dealEventMonth}
          onChange={(event) => setDealEventMonth(event.target.value)}
          className="w-full rounded-lg bg-muted px-2 py-1.5 text-[10px] outline-none"
        />
      )}
      <div className="flex justify-end gap-2 pt-0.5 text-[10px]">
        <button
          type="button"
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim() || !CURRENCY_CODE_PATTERN.test(operatingCurrency)}
          className="rounded-lg bg-foreground px-2.5 py-1 font-medium text-background disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
        >
          Add to perimeter
        </button>
      </div>
    </form>
  );
}

function annualCoverage(
  periods: { value: string; status: "received" | "missing" }[],
) {
  return periods.map((period) => ({
    label: coverageYearLabel(period.value),
    cells: [period.status],
  }));
}

function categoryCoverage(
  category: ReturnType<typeof categorizeSources>[number],
  financialYearEndMonth: number,
  onDownloadSource?: (source: BuilderLegalStructureSourceDocument) => void,
): LegalEntityFileCategory {
  const base = {
    label: category.label,
    files: category.sources.map((source) => ({
      id: source.document_id,
      name: source.filename,
      onOpen: onDownloadSource
        ? () => onDownloadSource(source)
        : undefined,
    })),
  };
  if (category.months.length > 0) {
    return {
      ...base,
      id: category.id,
      cadence: "monthly",
      coverage: monthlyCoverageRows(category.months, financialYearEndMonth),
    };
  }
  if (category.years.length > 0) {
    return {
      ...base,
      id: category.id,
      cadence: "annual",
      coverage: annualCoverage(category.years),
    };
  }
  return {
    ...base,
    id: category.id,
    cadence: category.cadence,
    coverage: COVERAGE_YEARS.map((label) => ({
      label,
      cells: Array.from<FileCoverageStatus>({
        length:
          category.cadence === "monthly"
            ? 12
            : category.cadence === "quarterly"
              ? 4
              : 1,
      }).map(() => "missing"),
    })),
  };
}

function buildFileCoverage(
  sources: BuilderLegalStructureSourceDocument[],
  financialYearEndMonth: number,
  onDownloadSource?: (source: BuilderLegalStructureSourceDocument) => void,
): LegalEntityFileCoverageProps {
  return {
    totalFiles: sources.length,
    categories: categorizeSources(sources, financialYearEndMonth).map(
      (category) =>
        categoryCoverage(category, financialYearEndMonth, onDownloadSource),
    ),
  };
}

function mergeSources(
  sources: BuilderLegalStructureSourceDocument[],
): BuilderLegalStructureSourceDocument[] {
  const merged = new Map<string, BuilderLegalStructureSourceDocument>();
  for (const source of sources) {
    const existing = merged.get(source.document_id);
    if (!existing) {
      merged.set(source.document_id, {
        ...source,
        sheets: [...source.sheets],
        table_types: [...source.table_types],
        document_types: [...source.document_types],
        period_coverage: [...source.period_coverage],
      });
      continue;
    }
    merged.set(source.document_id, {
      ...existing,
      sheets: Array.from(new Set([...existing.sheets, ...source.sheets])).sort(),
      table_types: Array.from(
        new Set([...existing.table_types, ...source.table_types]),
      ).sort(),
      document_types: Array.from(
        new Set([...existing.document_types, ...source.document_types]),
      ).sort(),
      period_coverage: [
        ...existing.period_coverage,
        ...source.period_coverage,
      ],
    });
  }
  return Array.from(merged.values());
}

function consolidationActivitySummary(
  sources: BuilderLegalStructureSourceDocument[],
): string {
  const tableTypes = new Set(sources.flatMap((source) => source.table_types));
  const hasPnl = tableTypes.has("pnl");
  const hasBalanceSheet = tableTypes.has("balance_sheet");
  if (hasPnl && hasBalanceSheet) {
    return "Group-level consolidated P&L and balance sheet.";
  }
  return hasPnl
    ? "Group-level consolidated P&L."
    : "Group-level consolidated balance sheet.";
}

export function LegalStructureEditor({
  value,
  evidence,
  consolidationSources = [],
  consolidationCurrencies = [],
  analysisInterval,
  editing,
  dirty = false,
  saving = false,
  attachmentsBusy = false,
  attachmentsInvalid = false,
  canEdit,
  error,
  onChange,
  onEdit,
  onSave,
  onConfirmPresentationCurrency,
  onCancel,
  onContinue,
  onDownloadSource,
  persistedEntityIds,
  draftAttachments = {},
  onAttachFiles,
  onDetachAttachment,
  onDownloadAttachment,
}: LegalStructureEditorProps) {
  const [addingEntity, setAddingEntity] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const draggedEntityId = useRef<string | null>(null);

  const activeEntities = value.entities.filter((entity) => entity.active);
  const removedEntities = value.entities.filter((entity) => !entity.active);
  const insideEntities = activeEntities.filter(
    (entity) => entity.transaction_perimeter === "inside",
  );
  const outsideEntities = activeEntities.filter(
    (entity) => entity.transaction_perimeter === "outside",
  );
  const insideIds = new Set(insideEntities.map((entity) => entity.id));
  const insideParentIds = new Set(
    insideEntities
      .map((entity) => entity.parent_entity_id)
      .filter((entityId): entityId is string => Boolean(entityId)),
  );
  const rootEntities = insideEntities.filter(
    (entity) =>
      insideParentIds.has(entity.id)
      && (
        entity.parent_entity_id === null
        || !insideIds.has(entity.parent_entity_id)
      ),
  );
  const rootIds = new Set(rootEntities.map((entity) => entity.id));
  const groupedInsideEntities = insideEntities.filter(
    (entity) => !rootIds.has(entity.id),
  );
  const validationErrors = validateLegalStructure(value);
  const orderedGroups = [...value.groups].sort(
    (left, right) => left.position - right.position,
  );
  const visibleGroups = orderedGroups.filter(
    (group) =>
      editing
      || groupedInsideEntities.some(
        (entity) => entity.display_group_id === group.id,
      ),
  );
  const ungroupedEntities = groupedInsideEntities.filter(
    (entity) => entity.display_group_id === null,
  );
  const visibleHighlightsByEntity = new Map(
    activeEntities.map((entity) => [
      entity.id,
      visibleLegalEntityHighlights(
        evidence[entity.id]?.highlights ?? [],
        entity.deal_events,
      ),
    ]),
  );
  const allHighlights = Array.from(
    new Set(
      activeEntities.flatMap(
        (entity) => visibleHighlightsByEntity.get(entity.id) ?? [],
      ),
    ),
  );
  const hasMidPeriodAcquisitions = activeEntities.some((entity) =>
    entity.deal_events.some((event) =>
      isMidPeriodAcquisition(event, analysisInterval)));
  const hasConsolidationMembers = insideEntities.length > 1
    && insideEntities.some(
      (entity) => evidence[entity.id]?.included_in_consolidation,
    );
  const hasMidPeriodDisposals = activeEntities.some((entity) =>
    entity.deal_events.some((event) =>
      isMidPeriodDisposal(event, analysisInterval)));
  const filters = [
    ...(hasConsolidationMembers
      ? [{
          id: "consolidation:included",
          label: "Included in consolidation",
        }]
      : []),
    ...(hasMidPeriodAcquisitions
      ? [{
          id: "event:mid-period-acquisition",
          label: "Mid-period acquisitions",
        }]
      : []),
    ...(hasMidPeriodDisposals
      ? [{ id: "event:mid-period-disposal", label: "Mid-period disposals" }]
      : []),
    ...allHighlights.map((highlight) => ({
      id: `highlight:${highlight}`,
      label: highlight,
    })),
  ];
  const activeFilter = selectedFilter !== null
    && filters.some((filter) => filter.id === selectedFilter)
    ? selectedFilter
    : null;
  const entityNames = new Map(
    activeEntities.map((entity) => [entity.id, entity.name]),
  );
  const activeEntitiesById = new Map(
    activeEntities.map((entity) => [entity.id, entity]),
  );
  const candidateConsolidationParentIds = new Set(
    activeEntities
      .filter(
        (entity) =>
          entity.parent_entity_id !== null
          && (
            entity.relationship_type === "consolidates"
            || entity.relationship_type === "subsidiary"
          ),
      )
      .map((entity) => entity.parent_entity_id as string),
  );
  const consolidationParentIds = new Set(
    Array.from(candidateConsolidationParentIds).filter((candidateId) => {
      const seen = new Set<string>();
      let parentId = activeEntitiesById.get(candidateId)?.parent_entity_id;
      while (parentId !== null && parentId !== undefined && !seen.has(parentId)) {
        if (candidateConsolidationParentIds.has(parentId)) {
          return false;
        }
        seen.add(parentId);
        parentId = activeEntitiesById.get(parentId)?.parent_entity_id;
      }
      return true;
    }),
  );
  const showStandaloneConsolidation = insideEntities.length > 1
    && consolidationSources.length > 0;
  const hasDraftAttachments = Object.values(draftAttachments).some(
    (attachments) => attachments.some((attachment) => attachment.status === "ready"),
  );
  const editorLocked = saving || attachmentsBusy;
  const editActionsEnabled = editing && !editorLocked;
  const dragEnabled = canEdit && !editorLocked;
  const presentationCurrencySources = Array.from(
    new Set(insideEntities.map((entity) => entity.operating_currency)),
  ).sort();

  const isHighlighted = (entity: BuilderLegalStructureEntity) =>
    activeFilter === null
    || (
      activeFilter === "consolidation:included"
      && evidence[entity.id]?.included_in_consolidation
    )
    || (
      activeFilter === "event:mid-period-acquisition"
      && entity.deal_events.some((event) =>
        isMidPeriodAcquisition(event, analysisInterval))
    )
    || (
      activeFilter === "event:mid-period-disposal"
      && entity.deal_events.some((event) =>
        isMidPeriodDisposal(event, analysisInterval))
    )
    || (
      activeFilter.startsWith("highlight:")
      && (visibleHighlightsByEntity.get(entity.id) ?? []).includes(
        activeFilter.slice("highlight:".length),
      )
    );

  const moveInside = (
    entityId: string,
    groupId: string | null,
    beforeEntityId?: string,
  ) => {
    const moved = moveLegalEntity(value, entityId, groupId, beforeEntityId);
    onChange(
      updateLegalEntity(moved, entityId, {
        transaction_perimeter: "inside",
      }),
    );
  };

  const renderCard = (entity: BuilderLegalStructureEntity) => {
    const entityEvidence = evidence[entity.id];
    const isConsolidatingParent = consolidationParentIds.has(entity.id);
    const sources = mergeSources(entityEvidence?.source_files ?? []);
    const sourceCurrencies = Array.from(
      new Set(entityEvidence?.source_currencies ?? []),
    ).sort();
    const parentName = entity.parent_entity_id
      ? entityNames.get(entity.parent_entity_id)
      : null;
    const isNewDraftEntity = persistedEntityIds !== undefined
      && !persistedEntityIds.has(entity.id);
    return (
      <LegalEntityCard
        name={entity.name}
        businessCurrency={entity.operating_currency}
        businessCurrencyLabel="Operating currency"
        sourceCurrencies={sourceCurrencies}
        reportsInto={parentName ? [parentName] : []}
        inPerimeter={entity.transaction_perimeter === "inside"}
        highlighted={isHighlighted(entity)}
        highlights={visibleHighlightsByEntity.get(entity.id) ?? []}
        dealEvents={entity.deal_events}
        analysisInterval={analysisInterval}
        activitySummary={entity.activity_summary}
        consolidatedGroup={isConsolidatingParent}
        fileCoverage={buildFileCoverage(
          sources,
          analysisInterval?.financialYearEndMonth ?? 12,
          onDownloadSource,
        )}
        userAddedMessage={entity.origin === "user" && sources.length === 0
          ? "No analysed source files are assigned to this entity."
          : undefined}
        draftAttachments={isNewDraftEntity ? draftAttachments[entity.id] : undefined}
        attachmentActionsDisabled={editorLocked}
        editable={editActionsEnabled}
        perimeterEditable={dragEnabled}
        parentEntityId={entity.parent_entity_id}
        relationshipType={entity.relationship_type}
        parentOptions={activeEntities
          .filter((candidate) => candidate.id !== entity.id)
          .map((candidate) => ({
            id: candidate.id,
            name: candidate.name,
          }))}
        onNameChange={(name) =>
          onChange(updateLegalEntity(value, entity.id, { name }))}
        onBusinessCurrencyChange={(operatingCurrency) =>
          onChange(
            updateLegalEntity(value, entity.id, {
              operating_currency: operatingCurrency,
            }),
          )}
        onRelationshipChange={(parentEntityId, relationshipType) =>
          onChange(
            updateLegalEntity(value, entity.id, {
              parent_entity_id: parentEntityId,
              relationship_type: relationshipType,
            }),
          )}
        onTogglePerimeter={() =>
          onChange(
            updateLegalEntity(value, entity.id, {
              transaction_perimeter:
                entity.transaction_perimeter === "inside"
                  ? "outside"
                  : "inside",
            }),
          )}
        onActivitySummaryChange={(activitySummary) =>
          onChange(
            updateLegalEntity(value, entity.id, {
              activity_summary: activitySummary,
            }),
          )}
        onDealEventsChange={(dealEvents) =>
          onChange(updateLegalEntity(value, entity.id, { deal_events: dealEvents }))}
        onRemove={() => onChange(removeLegalEntity(value, entity.id))}
        onAttachFiles={isNewDraftEntity && onAttachFiles
          ? (files) => onAttachFiles(entity.id, files)
          : undefined}
        onDetachAttachment={isNewDraftEntity && onDetachAttachment
          ? (attachmentKey) => onDetachAttachment(entity.id, attachmentKey)
          : undefined}
        onDownloadAttachment={isNewDraftEntity && onDownloadAttachment
          ? (attachment) => onDownloadAttachment(entity.id, attachment)
          : undefined}
      />
    );
  };

  const renderHierarchy = (
    entities: BuilderLegalStructureEntity[],
    groupId: string | null,
    allowDrop: boolean,
  ) => {
    const entityIds = new Set(entities.map((entity) => entity.id));
    const childrenByParent = new Map<string, BuilderLegalStructureEntity[]>();
    for (const entity of entities) {
      if (
        entity.parent_entity_id
        && entityIds.has(entity.parent_entity_id)
      ) {
        const children = childrenByParent.get(entity.parent_entity_id) ?? [];
        children.push(entity);
        childrenByParent.set(entity.parent_entity_id, children);
      }
    }
    const roots = entities.filter(
      (entity) =>
        !entity.parent_entity_id
        || !entityIds.has(entity.parent_entity_id),
    );
    const renderNode = (
      entity: BuilderLegalStructureEntity,
      nested = false,
    ): React.ReactNode => (
      <li
        key={entity.id}
        className={nested
          ? "relative before:absolute before:-left-6 before:top-7 before:h-px before:w-6 before:bg-border"
          : ""}
        draggable={dragEnabled}
        onDragStart={(event) => {
          if (!dragEnabled) return;
          draggedEntityId.current = entity.id;
          setDragging(true);
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", entity.id);
        }}
        onDragEnd={() => {
          draggedEntityId.current = null;
          setDragging(false);
        }}
        onDragOver={(event) => {
          if (!editActionsEnabled || !allowDrop) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onDrop={(event) => {
          if (!editActionsEnabled || !allowDrop || !draggedEntityId.current) return;
          event.preventDefault();
          event.stopPropagation();
          moveInside(draggedEntityId.current, groupId, entity.id);
          draggedEntityId.current = null;
          setDragging(false);
        }}
      >
        {renderCard(entity)}
        {(childrenByParent.get(entity.id)?.length ?? 0) > 0 && (
          <ul className="ml-5 space-y-3 border-l border-border pt-3 pl-6">
            {childrenByParent.get(entity.id)?.map((child) =>
              renderNode(child, true))}
          </ul>
        )}
      </li>
    );
    return (
      <ul className="flex max-w-full flex-wrap items-start gap-3">
        {roots.map((entity) => renderNode(entity))}
      </ul>
    );
  };

  const hasTopLayer = showStandaloneConsolidation || rootEntities.length > 0;
  const hasGroupLayer = visibleGroups.length > 0 || ungroupedEntities.length > 0;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="builder-stage-frame py-4">
        <div className="mb-4 flex items-start gap-3">
          <p className="flex-1 text-[11px] leading-relaxed text-muted-foreground">
            {insideEntities.length} selected into the transaction perimeter.
          </p>
          {canEdit && !editing && (
            <button
              type="button"
              onClick={onEdit}
              disabled={saving || attachmentsBusy}
              className="rounded-lg border border-border px-3 py-1.5 text-[11.5px] disabled:opacity-50"
            >
              Edit
            </button>
          )}
          {(editing || dirty) && (
            <>
              <button
                type="button"
                onClick={() => {
                  setAddingEntity(false);
                  onCancel();
                }}
                disabled={saving || attachmentsBusy}
                className="rounded-lg border border-border px-3 py-1.5 text-[11.5px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={
                  saving
                  || attachmentsBusy
                  || attachmentsInvalid
                  || !dirty
                  || validationErrors.length > 0
                }
                className="rounded-lg bg-foreground px-3 py-1.5 text-[11.5px] font-medium text-background disabled:opacity-50"
              >
                {saving
                  ? hasDraftAttachments ? "Analyzing files…" : "Saving…"
                  : "Save"}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onContinue}
            disabled={
              editing
              || dirty
              || saving
              || attachmentsBusy
              || !value.presentation_currency_confirmed
            }
            className="rounded-lg bg-foreground px-3 py-1.5 text-[12.5px] font-medium text-background disabled:opacity-50"
          >
            Continue
          </button>
        </div>

        {!value.presentation_currency_confirmed && (
          <div
            role="alert"
            className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50/70 px-3 py-2 text-[11px] text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/20 dark:text-amber-100"
          >
            <span className="mr-auto">
              Multiple currencies detected. Confirm the presentation currency
              before continuing.
            </span>
            <BusinessCurrencyPill
              value={value.presentation_currency}
              label="Presentation currency"
              sourceCurrencies={presentationCurrencySources}
              onChange={canEdit && !editorLocked
                ? (presentationCurrency) =>
                  onChange({
                    ...value,
                    presentation_currency: presentationCurrency,
                  })
                : undefined}
            />
            {canEdit && (
              <button
                type="button"
                onClick={onConfirmPresentationCurrency}
                disabled={
                  editorLocked
                  || attachmentsInvalid
                  || validationErrors.length > 0
                }
                className="rounded-md bg-amber-950 px-2.5 py-1 font-medium text-white disabled:opacity-50 dark:bg-amber-100 dark:text-amber-950"
              >
                {saving ? "Saving…" : "Confirm"}
              </button>
            )}
          </div>
        )}

        {(error || validationErrors.length > 0) && (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-[11px] text-destructive">
            {error || validationErrors[0]?.message}
          </div>
        )}

        {filters.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {[null, ...filters].map((filter) => (
              <button
                key={filter?.id ?? "all"}
                type="button"
                onClick={() => setSelectedFilter(filter?.id ?? null)}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-medium ${
                  activeFilter === (filter?.id ?? null)
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {filter?.label ?? "All"}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col items-center pb-6">
          <section
            className={`relative mt-8 flex w-full flex-col items-center rounded-3xl border-2 border-dashed px-6 pb-5 pt-10 transition-colors ${
              dragging && dragEnabled
                ? "border-foreground bg-muted/30"
                : "border-foreground/35"
            }`}
            onDragOver={(event) => {
              if (dragEnabled) event.preventDefault();
            }}
            onDrop={(event) => {
              if (!dragEnabled || !draggedEntityId.current) return;
              event.preventDefault();
              onChange(
                updateLegalEntity(value, draggedEntityId.current, {
                  transaction_perimeter: "inside",
                }),
              );
              draggedEntityId.current = null;
              setDragging(false);
            }}
          >
            <h2 className="absolute left-[22px] top-[-10px] rounded bg-background px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
              Transaction perimeter, {insideEntities.length} of{" "}
              {activeEntities.length} companies
            </h2>

            {showStandaloneConsolidation && (
              <div aria-label="Consolidation sources">
                <LegalEntityCard
                  name="Consolidation"
                  businessCurrency={value.presentation_currency}
                  businessCurrencyLabel="Presentation currency"
                  sourceCurrencies={consolidationCurrencies}
                  highlights={["Consolidation"]}
                  activitySummary={consolidationActivitySummary(
                    consolidationSources,
                  )}
                  fileCoverage={buildFileCoverage(
                    consolidationSources,
                    analysisInterval?.financialYearEndMonth ?? 12,
                    onDownloadSource,
                  )}
                  editable={editActionsEnabled}
                  onBusinessCurrencyChange={(presentationCurrency) =>
                    onChange({
                      ...value,
                      presentation_currency: presentationCurrency,
                    })}
                />
              </div>
            )}

            {showStandaloneConsolidation
              && (rootEntities.length > 0 || hasGroupLayer || editing) && (
              <div className="h-5 w-px bg-muted" />
            )}

            {rootEntities.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4">
                {rootEntities.map((entity) => (
                  <div
                    key={entity.id}
                    draggable={dragEnabled}
                    onDragStart={(event) => {
                      if (!dragEnabled) return;
                      draggedEntityId.current = entity.id;
                      setDragging(true);
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", entity.id);
                    }}
                    onDragEnd={() => {
                      draggedEntityId.current = null;
                      setDragging(false);
                    }}
                  >
                    {renderCard(entity)}
                  </div>
                ))}
              </div>
            )}

            {rootEntities.length > 0 && (hasGroupLayer || editing) && (
              <div className="h-5 w-px bg-muted" />
            )}

            {(hasGroupLayer || editing) && (
              <div
                className={`relative flex w-full flex-wrap items-start justify-center gap-5 ${
                  hasTopLayer ? "pt-5" : ""
                }`}
              >
                {hasTopLayer
                  && visibleGroups.length
                    + (ungroupedEntities.length > 0 ? 1 : 0)
                    + (editing ? 1 : 0) > 1 && (
                  <div className="absolute left-[130px] right-[130px] top-0 h-px bg-border" />
                )}
                {[
                  ...visibleGroups.map((group) => ({
                    id: group.id as string | null,
                    name: group.name,
                    entities: groupedInsideEntities.filter(
                      (entity) => entity.display_group_id === group.id,
                    ),
                  })),
                  ...(ungroupedEntities.length > 0
                    ? [{
                        id: null,
                        name: "",
                        entities: ungroupedEntities,
                      }]
                    : []),
                ].map((section) => (
                  <section key={section.id ?? "ungrouped"} className="relative">
                    {hasTopLayer && (
                      <span className="absolute left-1/2 top-[-20px] h-5 w-px bg-muted" />
                    )}
                    <div
                      className={`rounded-2xl border p-3 ${
                        editActionsEnabled ? "border-dashed" : ""
                      }`}
                      onDragOver={(event) => {
                        if (!editActionsEnabled) return;
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDrop={(event) => {
                        if (!editActionsEnabled || !draggedEntityId.current) return;
                        event.preventDefault();
                        event.stopPropagation();
                        moveInside(draggedEntityId.current, section.id);
                        draggedEntityId.current = null;
                        setDragging(false);
                      }}
                    >
                      <div className="mb-2 flex items-center gap-1">
                        {editActionsEnabled && section.id ? (
                          <input
                            aria-label={`Group name: ${section.name}`}
                            value={section.name}
                            onChange={(event) =>
                              onChange(
                                updateLegalGroup(value, section.id!, {
                                  name: event.target.value,
                                }),
                              )}
                            className="w-40 rounded bg-muted px-1 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide outline-none"
                          />
                        ) : section.name ? (
                          <h3 className="w-40 text-[10.5px] font-semibold uppercase tracking-wide">
                            {section.name}
                          </h3>
                        ) : null}
                      </div>
                      {section.entities.length > 0
                        ? renderHierarchy(section.entities, section.id, true)
                        : (
                          <div className="flex h-[96px] w-[236px] items-center justify-center px-2 text-[9.5px] italic text-muted-foreground/50">
                            drop a company here
                          </div>
                        )}
                    </div>
                  </section>
                ))}
                {editActionsEnabled && (
                  <div className="relative">
                    {hasTopLayer && (
                      <span className="absolute left-1/2 top-[-20px] h-5 w-px bg-muted" />
                    )}
                    {addingEntity ? (
                      <AddLegalEntityCard
                        groups={orderedGroups}
                        onAdd={(name, options) => {
                          onChange(addLegalEntity(value, name, options));
                          setAddingEntity(false);
                        }}
                        onCancel={() => setAddingEntity(false)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingEntity(true)}
                        className="flex h-[120px] w-[236px] items-center justify-center gap-1.5 rounded-xl border border-dashed border-border text-[11px] text-muted-foreground hover:border-foreground hover:text-foreground"
                      >
                        <Plus size={12} /> Add a missing entity
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

          </section>

          <section
            className={`mt-8 min-h-[140px] w-full rounded-2xl border-2 border-dashed p-5 transition-colors ${
              dragging && dragEnabled
                ? "border-foreground bg-muted/60"
                : "border-foreground/25 hover:border-foreground/40"
            }`}
            onDragOver={(event) => {
              if (dragEnabled) event.preventDefault();
            }}
            onDrop={(event) => {
              if (!dragEnabled || !draggedEntityId.current) return;
              event.preventDefault();
              onChange(
                updateLegalEntity(value, draggedEntityId.current, {
                  transaction_perimeter: "outside",
                }),
              );
              draggedEntityId.current = null;
              setDragging(false);
            }}
          >
            <h2 className="mb-3 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground/70">
              {dragging && dragEnabled
                ? "Drop here to exclude it from the financial build"
                : "Outside the transaction perimeter"}
            </h2>
            {outsideEntities.length > 0 ? (
              renderHierarchy(outsideEntities, null, false)
            ) : (
              <p className="w-full px-2 py-4 text-center text-[11px] italic text-muted-foreground">
                Nothing is excluded. Drag a company here to keep its review
                evidence but exclude it from the financial build.
              </p>
            )}
          </section>

          {removedEntities.length > 0 && (
            <div className="mt-4 text-[10.5px] text-muted-foreground">
              Removed from the chart:{" "}
              {removedEntities.map((entity) => (
                <span key={entity.id} className="mr-3">
                  {entity.name}{" "}
                  {editActionsEnabled && (
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() =>
                        onChange(
                          updateLegalEntity(value, entity.id, {
                            active: true,
                            position: 0,
                          }),
                        )}
                    >
                      restore
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
